<script setup lang="ts">
import { showLoadingToast, closeToast } from 'vant'
import { ref, onMounted, watch, computed } from 'vue'
import liff from '@line/liff'
import {
  // getSubdomainFromHost,
  getParamsFromLiffUrl,
  getAPIUrlFromClinicType,
  validateRequiredParams,
  setUpCrmUrl,
} from '@/utils'
import { useAppStore } from '@/stores/AppStore'
import { i18nInitialized, initAppLanguage } from '@/i18n'
import VConsole from 'vconsole'
import { LineLiffProfile } from './type/Line'
import 'vant/es/toast/style'
import { vantTheme, themeColor, hexToRgb } from './theme'
import vuetify from './vuetify'
import { is_line_express } from './stores/Constant'

const appStore = useAppStore()
const ready = ref<boolean>(false)
const loading = ref<boolean>(true)

const setupVConsole = () => {
  // ถ้าไม่ได้เปิด VConsole จะไม่ทำอะไร
  if (!import.meta.env.VITE_VCONSOLE_ENABLE) return

  try {
    new VConsole({ theme: 'dark' })
  } catch (error) {
    console.error(`VConsole setup failed. ${error}`)
  }
}

const initialize = async () => {
  /*
    .::NOTE::.
    ต้องเข้าด้วย
    https://liff.line.me/<LIFF_ID>/<PATH>/?liffId=<LIFF_ID>&clinicType=<CLINIC_TYPE>&c=<CLINIC_UUID>
    Params ที่ต้องใส่มากทุกครั้งเวลาเรียก liff คือ
    1. liffId
    2. clinicType
    3. clinicUUID
  */
  loading.value = true
  setupVConsole()
  try {
    appStore.setCrmUrl(setUpCrmUrl())
  } catch (error) {
    ready.value = false
    loading.value = false
    return
  }
  try {
    const params = getParamsFromLiffUrl(window.location.search)
    validateRequiredParams(['clinicType', 'c', 'liffId', 'channelId'], params)
    await liff.init({ liffId: params.get('liffId') || '' })
    // ถ้าเจอ error LiffId is not found. ก็คือ API getProfile()
    if (!liff.isLoggedIn()) {
      liff.login({ redirectUri: window.location.href })
    } else {
      // save liff id
      appStore.setLiffId(params.get('liffId') as string)
      // toggle express
      const channelId = params.get('channelId') as string
      const isXpress = is_line_express(channelId)
      appStore.setChannelId(channelId)
      appStore.setIsXpress(isXpress)
      // clinic data
      const clinic_uuid = params.get('c') as string
      appStore.setUrl(getAPIUrlFromClinicType(params.get('clinicType') as string))
      appStore.setClinicUUID(clinic_uuid)

      // user data
      appStore.setUserProfile((await liff.getProfile()) as LineLiffProfile)
      appStore.setIsInit(false) // set isInit to false after first load, so it won't be true again until next refresh
      initAppLanguage()
      await i18nInitialized({ ns: clinic_uuid })
      ready.value = true
    }
  } catch (e) {
    console.error(`LIFF init failed. ${e}`)
    if (import.meta.env.PROD) {
      window.location.href = 'https://jeracloud.com'
    }
  } finally {
    loading.value = false
  }
}

const themeVars = computed(() => {
  // change vant and vuetify theme
  if (appStore.isXpress) {
    vuetify.theme.global.name.value = 'xpress'
    return vantTheme.xpress
  }
  return vantTheme.aura
})

// CSS variables for Tailwind dynamic theme
// เปลี่ยนอะไรตรงนี้แล้วไปเปลี่บนนใน tailwind.config.js ด้วยนะ
const themeCssVars = computed(() => {
  const colors = appStore.isXpress ? themeColor.xpress : themeColor.aura
  return {
    '--color-primary': hexToRgb(colors.primary),
    '--color-primary2': hexToRgb(colors.primary2),
    '--color-secondary': hexToRgb(colors.secondary),
    '--color-background': hexToRgb(colors.background),
  }
})

onMounted(() => {
  initialize()
})

watch(
  loading,
  loading => {
    if (loading) {
      showLoadingToast({
        // message: t('loading'),
        forbidClick: true,
        duration: 0,
        loadingType: 'spinner',
        overlay: true,
      })
    } else {
      closeToast()
    }
  },
  { immediate: true },
)
</script>

<template>
  <van-config-provider :theme-vars="themeVars">
    <div :style="themeCssVars">
      <van-overlay :show="!ready && !loading">
        <div class="wrapper">
          <div class="block">
            <h2>{{ $t('something_went_wrong') }}</h2>
          </div>
        </div>
      </van-overlay>
      <router-view v-if="ready" v-slot="{ Component }">
        <component :is="Component" :key="$route.path"></component>
      </router-view>
    </div>
  </van-config-provider>
</template>

<style lang="css">
html,
body {
  font-family: 'Kanit';
  font-size: 14px;
  background-color: #fff;
  /* background-color: #f8f8f8; */
  -webkit-font-smoothing: antialiased;
}
.wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.block {
  border-radius: 25px;
  width: 300px;
  height: 300px;
  background-color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
